/**
 * Invoice list hook (REV-040 → REV-049).
 *
 * Wraps the Revenue service's invoice-list surface plus the per-invoice
 * lifecycle actions:
 *   - GET  /api/v1/invoices                 → Page<RevenueInvoice> (tenant-scoped)
 *   - POST /api/v1/invoices/{id}:approve    → route a draft into finance review
 *   - POST /api/v1/invoices/{id}:submit     → approve/submit (emits INVOICE_APPROVED)
 *
 * The list endpoint is a small top-up to the Revenue service. If it is not yet
 * deployed (404), the hook surfaces an empty list with `unavailable=true` so
 * the page can render a graceful empty state instead of an error.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { RevenueInvoice } from './useRevenue';

interface UseInvoicesResult {
  invoices: RevenueInvoice[];
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  /** True when the list endpoint 404s (not yet available) — render empty state. */
  unavailable: boolean;
  refresh: () => void;
  approveInvoice: (invoiceId: string) => Promise<RevenueInvoice | null>;
  submitInvoice: (invoiceId: string) => Promise<RevenueInvoice | null>;
}

interface InvoicePage {
  items: RevenueInvoice[];
  next_cursor?: string | null;
}

/** Replace an invoice in the list with its latest server projection. */
function upsert(list: RevenueInvoice[], next: RevenueInvoice): RevenueInvoice[] {
  return list.map((inv) => (inv.invoice_id === next.invoice_id ? next : inv));
}

/** Loads the tenant's invoices and exposes approve/submit lifecycle actions. */
export function useInvoices(): UseInvoicesResult {
  const [invoices, setInvoices] = useState<RevenueInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setUnavailable(false);
    apiClient
      .get<InvoicePage>('/invoices')
      .then((page) => {
        setInvoices(page.items ?? []);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          // List endpoint not deployed yet — graceful empty state.
          setUnavailable(true);
          setInvoices([]);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setInvoices([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = useCallback(
    async (op: () => Promise<RevenueInvoice>): Promise<RevenueInvoice | null> => {
      setIsWorking(true);
      setError(null);
      try {
        const invoice = await op();
        setInvoices((prev) => upsert(prev, invoice));
        return invoice;
      } catch (err: unknown) {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    []
  );

  const approveInvoice = useCallback(
    (invoiceId: string) =>
      run(() =>
        apiClient.post<RevenueInvoice>(`/invoices/${encodeURIComponent(invoiceId)}:approve`)
      ),
    [run]
  );

  const submitInvoice = useCallback(
    (invoiceId: string) =>
      run(() =>
        apiClient.post<RevenueInvoice>(`/invoices/${encodeURIComponent(invoiceId)}:submit`)
      ),
    [run]
  );

  return {
    invoices,
    isLoading,
    isWorking,
    error,
    unavailable,
    refresh,
    approveInvoice,
    submitInvoice,
  };
}
